import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import ImageDropzone from "components/ImageDropzone";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeTenant, updateTenant } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  tenantId: string;
}

const initialValues = {
  name: "",
};

const TenantFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const isAddMode = !tenantId;
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;

  const [filesLogo, setFilesLogo] = useState<any>([]);

  const { appUrl } = useSelector((state: any) => state.globalReducer);
  const { isFormLoading } = useSelector((state: any) => state.tenantReducer);
  const { tenants } = useSelector((state: any) => state.tenantReducer);

  const handleCloseModal = () => {
    onClose && onClose();
    setFilesLogo([]);
  };

  const handleSubmit = (values: any) => {
    const { name } = values;
    const logo = typeof filesLogo[0] === "object" ? filesLogo[0] : null;

    isAddMode
      ? dispatch(storeTenant({ name, logo }, handleCloseModal) as any)
      : dispatch(
          updateTenant(tenantId, { name, logo }, handleCloseModal) as any
        );
  };

  useEffect(() => {
    if (!isAddMode) {
      const tenantDetail = tenants.find((item: any) => item.id === tenantId);

      if (formikRef.current) {
        formikRef.current.setFieldValue("name", tenantDetail.name);

        setFilesLogo(
          tenantDetail.logo ? [appUrl + "/storage/" + tenantDetail.logo] : []
        );
      }
    }
  }, [isAddMode, tenants, tenantId, appUrl]);

  const validationSchema = yup.object({
    name: yup.string().required("Name is required field"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {() => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>
                  {isAddMode ? "Add Tenant" : "Update Tenant"}
                </Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="name">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="name">Name</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Stack spacing="xs">
                    <FormLabel>Logo</FormLabel>
                    <ImageDropzone files={filesLogo} setFiles={setFilesLogo} />
                  </Stack>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    {isAddMode ? "Add" : "Update"}
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default TenantFormModal;
